import { Zap } from "lucide-react"
import { useLocation, useNavigate } from "react-router-dom"
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar"
import { SidebarGlyphSlot } from "@/components/sidebar/sidebar-glyph-slot"
import { cn } from "@/lib/utils"
import {
  getUpgradePlanConfig,
  resolveUpgradePlanPath,
  shouldShowUpgradeCta,
} from "@/constants/upgrade-plan"
import { useEmployerOrganisationOptional } from "@/context/employer-organisation-context"
import { useFocusStore } from "@/hooks/use-focus-store"
import { useOrganisationBilling } from "@/hooks/use-plans"

interface SidebarUpgradeCtaProps {
  isAuthenticated: boolean
  isCollapsed?: boolean
  onOpenSettings?: (path: string) => void
  className?: string
}

export function SidebarUpgradeCta({
  isAuthenticated,
  isCollapsed = false,
  onOpenSettings,
  className,
}: SidebarUpgradeCtaProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const upgradePlan = getUpgradePlanConfig()
  const { focus } = useFocusStore()
  const isEmployerFocus = focus?.current_focus === "employer"
  const employerOrg = useEmployerOrganisationOptional()
  const organisationSlug = employerOrg?.activeOrganisationSlug ?? undefined
  const { data: billing } = useOrganisationBilling(organisationSlug)

  if (
    !shouldShowUpgradeCta(upgradePlan, {
      isAuthenticated,
      isEmployerFocus,
      hasPaidPlan: billing?.has_paid_plan,
    })
  ) {
    return null
  }

  const billingPath = resolveUpgradePlanPath(organisationSlug)

  const handleUpgrade = () => {
    if (upgradePlan.externalUrl) {
      window.open(upgradePlan.externalUrl, "_blank", "noopener,noreferrer")
      return
    }
    if (onOpenSettings) {
      onOpenSettings(billingPath)
      return
    }
    navigate(billingPath, {
      state: { from: `${location.pathname}${location.search}` },
    })
  }

  return (
    <SidebarMenu className={cn("px-2", className)}>
      <SidebarMenuItem>
        <SidebarMenuButton
          onClick={handleUpgrade}
          aria-label={upgradePlan.ctaHeadline}
          tooltip={upgradePlan.ctaHeadline}
          className="group/upgrade border-primary/15 bg-primary/5 font-medium text-primary hover:!border-primary/25 hover:!bg-primary/10 hover:!text-primary"
        >
          <SidebarGlyphSlot>
            <Zap className="size-4 shrink-0 text-primary" />
          </SidebarGlyphSlot>
          {!isCollapsed && (
            <span className="min-w-0 truncate">
              {billing?.upgrade_target
                ? `Upgrade to ${billing.upgrade_target.display_name}`
                : upgradePlan.ctaHeadline}
            </span>
          )}
        </SidebarMenuButton>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}
